import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { auth } from "../services/firebase";
import {
  User,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut as firebaseSignOut,
  sendPasswordResetEmail,
  sendEmailVerification,
  GoogleAuthProvider,
  OAuthProvider,
  signInWithPopup,
  signInWithCredential,
  getAdditionalUserInfo,
  EmailAuthProvider,
  reauthenticateWithCredential,
  reauthenticateWithPopup,
} from "firebase/auth";
import { Capacitor } from "@capacitor/core";
import { FirebaseAuthentication } from "@capacitor-firebase/authentication";
import { trackEvent } from "../services/analytics";

interface AuthContextType {
  user: User | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  signInWithApple: () => Promise<void>;
  resendVerification: () => Promise<void>;
  // Friss hitelesítés kéréshez (auth/requires-recent-login), pl. fiók törlés előtt.
  // Jelszavas usernél kötelező a password paraméter, Google/Apple-nél figyelmen kívül marad.
  reauthenticate: (password?: string) => Promise<void>;
}


const AuthContext = createContext<AuthContextType | null>(null);
const googleProvider = new GoogleAuthProvider();
const appleProvider = new OAuthProvider("apple.com");

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (u) => {
      setUser(u);
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  const signIn = async (email: string, password: string) => {
    await signInWithEmailAndPassword(auth, email, password);
    trackEvent("login", { method: "email" });
  };

  const signUp = async (email: string, password: string) => {
    const credential = await createUserWithEmailAndPassword(auth, email, password);
    await sendEmailVerification(credential.user);
    trackEvent("sign_up", { method: "email" });
  };

  const signOut = async () => {
    await firebaseSignOut(auth);
  };

  const resetPassword = async (email: string) => {
    await sendPasswordResetEmail(auth, email);
  };

  const signInWithGoogle = async () => {
    let credential;
    if (Capacitor.isNativePlatform()) {
      const result = await FirebaseAuthentication.signInWithGoogle();
      if (!result.credential?.idToken) {
        const err = new Error("auth/no-credential") as Error & { code: string };
        err.code = "auth/no-credential";
        throw err;
      }
      const webCredential = GoogleAuthProvider.credential(
        result.credential.idToken,
        result.credential.accessToken
      );
      credential = await signInWithCredential(auth, webCredential);
    } else {
      credential = await signInWithPopup(auth, googleProvider);
    }
    trackEvent(getAdditionalUserInfo(credential)?.isNewUser ? "sign_up" : "login", { method: "google" });
  };

  const signInWithApple = async () => {
    let credential;
    if (Capacitor.isNativePlatform()) {
      // Natív iOS: @capacitor-firebase/authentication kezeli a nonce-t is
      const result = await FirebaseAuthentication.signInWithApple();
      if (!result.credential?.idToken) {
        const err = new Error("auth/no-credential") as Error & { code: string };
        err.code = "auth/no-credential";
        throw err;
      }
      const appleCredential = appleProvider.credential({
        idToken: result.credential.idToken,
        rawNonce: result.credential.nonce,
      });
      credential = await signInWithCredential(auth, appleCredential);
    } else {
      // Web: Firebase popup flow
      appleProvider.addScope("email");
      appleProvider.addScope("name");
      credential = await signInWithPopup(auth, appleProvider);
    }
    trackEvent(getAdditionalUserInfo(credential)?.isNewUser ? "sign_up" : "login", { method: "apple" });
  };

  const resendVerification = async () => {
    if (auth.currentUser) {
      await sendEmailVerification(auth.currentUser);
    }
  };

  const reauthenticate = async (password?: string) => {
    if (!auth.currentUser) {
      const err = new Error("auth/no-current-user") as Error & { code: string };
      err.code = "auth/no-current-user";
      throw err;
    }
    const providerId = auth.currentUser.providerData[0]?.providerId;

    if (providerId === "password") {
      if (!password) {
        const err = new Error("auth/missing-password") as Error & { code: string };
        err.code = "auth/missing-password";
        throw err;
      }
      const credential = EmailAuthProvider.credential(auth.currentUser.email!, password);
      await reauthenticateWithCredential(auth.currentUser, credential);
      return;
    }

    if (providerId === "google.com") {
      if (Capacitor.isNativePlatform()) {
        const result = await FirebaseAuthentication.signInWithGoogle();
        if (!result.credential?.idToken) {
          const err = new Error("auth/no-credential") as Error & { code: string };
          err.code = "auth/no-credential";
          throw err;
        }
        const webCredential = GoogleAuthProvider.credential(
          result.credential.idToken,
          result.credential.accessToken
        );
        await reauthenticateWithCredential(auth.currentUser, webCredential);
      } else {
        await reauthenticateWithPopup(auth.currentUser, googleProvider);
      }
      return;
    }

    if (providerId === "apple.com") {
      if (Capacitor.isNativePlatform()) {
        const result = await FirebaseAuthentication.signInWithApple();
        if (!result.credential?.idToken) {
          const err = new Error("auth/no-credential") as Error & { code: string };
          err.code = "auth/no-credential";
          throw err;
        }
        const appleCredential = appleProvider.credential({
          idToken: result.credential.idToken,
          rawNonce: result.credential.nonce,
        });
        await reauthenticateWithCredential(auth.currentUser, appleCredential);
      } else {
        await reauthenticateWithPopup(auth.currentUser, appleProvider);
      }
      return;
    }

    const err = new Error("auth/unsupported-provider") as Error & { code: string };
    err.code = "auth/unsupported-provider";
    throw err;
  };

  return (
    <AuthContext.Provider value={{
      user, loading, signIn, signUp, signOut,
      resetPassword, signInWithGoogle, signInWithApple, resendVerification,
      reauthenticate,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
